/**
 * One source's way from text to auto-sent answers, as four steps: generating its question and answer pairs,
 * checking (and editing) them, promoting them to serve as drafts, and auto-send, earned per item by drafts
 * sent unchanged. Each step says how far this source is and names the first item that needs work there.
 * Pure: it reads the source's items and decides; nothing here looks at any other source.
 */

import { stageOf, type Stage } from './status';
import type { KnowledgeItem } from './types';

export type SourceStepId = 'generate' | 'check' | 'promote' | 'auto_send';
export type SourceStepState = 'done' | 'attention' | 'todo';

export interface SourceStep {
	id: SourceStepId;
	label: string;
	state: SourceStepState;
	detail: string;
	/** The item this step's work is on, first; null when there is none. */
	itemId: string | null;
}

/** The groups the drawer lists the source's items in, in the order they move through. */
export const SOURCE_GROUPS: Array<{ label: string; stages: Stage[] }> = [
	{
		label: 'Needs work',
		stages: ['needs_check', 'check_failed', 'article_changed', 'edit_waiting', 'serving_check_failed']
	},
	{ label: 'Ready to promote', stages: ['ready'] },
	{ label: 'Serving as drafts', stages: ['draft'] },
	{ label: 'Auto-sends', stages: ['auto_send'] },
	{ label: 'Switched off', stages: ['off'] }
];

const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`;

export const sourceSteps = (items: KnowledgeItem[], hasText: boolean): SourceStep[] => {
	const stages = items.map((item) => ({ item, stage: stageOf(item) }));
	const first = (...wanted: Stage[]) => stages.find(({ stage }) => wanted.includes(stage))?.item.id ?? null;
	const count = (...wanted: Stage[]) => stages.filter(({ stage }) => wanted.includes(stage)).length;
	const total = items.length;
	const needWork = count('needs_check', 'check_failed', 'article_changed', 'edit_waiting', 'serving_check_failed');
	const ready = count('ready');
	const promoted = items.filter((item) => item.state === 'promoted').length;
	const auto = count('auto_send');
	// The promoted item closest to earning auto-send, for the step to name.
	const closest = items.filter((item) => stageOf(item) === 'draft').sort((a, b) => b.streak.count - a.streak.count)[0];

	const generate: SourceStep = {
		id: 'generate',
		label: 'Generate',
		state: total > 0 ? 'done' : hasText ? 'attention' : 'todo',
		detail:
			total > 0
				? `${plural(total, 'question')} from this source`
				: hasText
					? 'Generate its questions and answers'
					: 'No indexed text yet',
		itemId: null
	};

	const check: SourceStep = {
		id: 'check',
		label: 'Check & edit',
		state: total === 0 ? 'todo' : needWork > 0 ? 'attention' : 'done',
		detail:
			total === 0 ? 'Generate first' : needWork > 0 ? `${needWork} need work · ${ready} ready` : 'Every item checked',
		itemId: first('needs_check', 'check_failed', 'article_changed', 'edit_waiting', 'serving_check_failed')
	};

	const promote: SourceStep = {
		id: 'promote',
		label: 'Promote',
		state: promoted > 0 && ready === 0 ? 'done' : ready > 0 ? 'attention' : 'todo',
		detail:
			total === 0 ? 'Generate first' : `${promoted} of ${total} serving${ready ? ` · ${ready} ready to promote` : ''}`,
		itemId: first('ready')
	};

	const autoSend: SourceStep = {
		id: 'auto_send',
		label: 'Auto-send',
		state: promoted === 0 ? 'todo' : auto === promoted ? 'done' : 'attention',
		detail:
			promoted === 0
				? 'Promote first'
				: closest
					? `${auto} of ${promoted} auto-send · next ${Math.min(closest.streak.count, closest.streak.required)} of ${closest.streak.required} sent unchanged`
					: `${auto} of ${promoted} auto-send`,
		itemId: closest?.id ?? first('auto_send')
	};

	return [generate, check, promote, autoSend];
};
